import { useEffect, useId, useRef, useState, type JSX } from 'react';

/**
 * Dependency-free time-series chart (line or bar) used by the chart surface.
 * Replaces Recharts, whose internal store looped under React 19 ("Maximum update depth exceeded").
 * Accessible: the SVG has a title/description, every point has a <title>, and a data table follows the chart.
 */
export interface SvgSeries {
  name: string;
  color: string;
  points: Array<{ x: string; label: string; y: number; flag?: string }>;
}

export interface SvgChartProps {
  kind: 'line' | 'bar';
  series: SvgSeries[];
  unit?: string;
  referenceRange?: { low?: number; high?: number };
  height?: number;
  title: string;
}

const PAD = { top: 12, right: 16, bottom: 34, left: 48 };

function useMeasuredWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const w = Math.floor(el.getBoundingClientRect().width);
      setWidth((prev) => (Math.abs(prev - w) > 1 ? w : prev));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) return [min];
  const span = max - min;
  const rough = span / count;
  const pow = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => span / s <= count + 1) ?? pow;
  const start = Math.ceil(min / step) * step;
  const out: number[] = [];
  for (let v = start; v <= max + 1e-9; v += step) out.push(Number(v.toFixed(6)));
  return out;
}

export function SvgChart({ kind, series, unit, referenceRange, height = 260, title }: SvgChartProps): JSX.Element {
  const [boxRef, width] = useMeasuredWidth();
  const [hover, setHover] = useState<{ s: number; i: number } | null>(null);
  const id = useId();
  const xs = Array.from(new Set(series.flatMap((s) => s.points.map((p) => p.x)))).sort();
  const ys = series.flatMap((s) => s.points.map((p) => p.y));
  const lo = Math.min(...ys, referenceRange?.low ?? Infinity);
  const hi = Math.max(...ys, referenceRange?.high ?? -Infinity);
  const padY = (hi - lo || Math.abs(hi) || 1) * 0.15;
  const yMin = kind === 'bar' ? Math.min(0, lo) : lo - padY;
  const yMax = hi + padY;
  const innerW = Math.max(0, width - PAD.left - PAD.right);
  const innerH = Math.max(0, height - PAD.top - PAD.bottom);
  const xPos = (x: string) => {
    const i = xs.indexOf(x);
    if (xs.length <= 1) return PAD.left + innerW / 2;
    return PAD.left + (i / (xs.length - 1)) * innerW;
  };
  const yPos = (y: number) => PAD.top + innerH - ((y - yMin) / (yMax - yMin || 1)) * innerH;
  const ticks = niceTicks(yMin, yMax);
  const labelEvery = Math.max(1, Math.ceil(xs.length / Math.max(1, Math.floor(innerW / 80))));
  const barW = xs.length > 0 ? Math.min(28, (innerW / xs.length) * 0.6) : 0;
  const hovered = hover ? series[hover.s]?.points[hover.i] : undefined;
  const hoveredSeries = hover ? series[hover.s] : undefined;

  return (
    <div className="chart-box" ref={boxRef} style={{ position: 'relative' }}>
      {width > 0 && xs.length > 0 ? (
        <svg width={width} height={height} role="img" aria-labelledby={`${id}-t`} aria-describedby={`${id}-d`} onMouseLeave={() => setHover(null)}>
          <title id={`${id}-t`}>{title}</title>
          <desc id={`${id}-d`}>{`${series.map((s) => `${s.name}: ${s.points.length} points`).join('; ')}${unit ? `, unit ${unit}` : ''}`}</desc>
          {referenceRange && referenceRange.low !== undefined && referenceRange.high !== undefined ? (
            <rect x={PAD.left} y={yPos(referenceRange.high)} width={innerW} height={Math.max(0, yPos(referenceRange.low) - yPos(referenceRange.high))} fill="#1baf7a" fillOpacity={0.12} />
          ) : null}
          {referenceRange && referenceRange.low !== undefined && referenceRange.high === undefined ? <line x1={PAD.left} x2={PAD.left + innerW} y1={yPos(referenceRange.low)} y2={yPos(referenceRange.low)} stroke="#1baf7a" strokeDasharray="4 4" /> : null}
          {referenceRange && referenceRange.high !== undefined && referenceRange.low === undefined ? <line x1={PAD.left} x2={PAD.left + innerW} y1={yPos(referenceRange.high)} y2={yPos(referenceRange.high)} stroke="#1baf7a" strokeDasharray="4 4" /> : null}
          {ticks.map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + innerW} y1={yPos(t)} y2={yPos(t)} stroke="var(--border, #e5e7eb)" strokeDasharray="2 4" />
              <text x={PAD.left - 6} y={yPos(t) + 4} textAnchor="end" fontSize={11} fill="var(--text-muted, #6b7280)">{t}</text>
            </g>
          ))}
          <line x1={PAD.left} x2={PAD.left + innerW} y1={PAD.top + innerH} y2={PAD.top + innerH} stroke="var(--text-muted, #6b7280)" />
          {xs.map((x, i) =>
            i % labelEvery === 0 || i === xs.length - 1 ? (
              <text key={x} x={xPos(x)} y={PAD.top + innerH + 16} textAnchor="middle" fontSize={11} fill="var(--text-muted, #6b7280)">
                {series.flatMap((s) => s.points).find((p) => p.x === x)?.label ?? x}
              </text>
            ) : null,
          )}
          {series.map((s, si) => {
            const pts = s.points.filter((p) => xs.includes(p.x)).sort((a, b) => (a.x < b.x ? -1 : 1));
            if (kind === 'bar') {
              const offset = (si - (series.length - 1) / 2) * (barW + 2);
              return pts.map((p, i) => (
                <rect key={`${s.name}-${p.x}`} x={xPos(p.x) - barW / 2 + offset} y={Math.min(yPos(p.y), yPos(0))} width={barW} height={Math.abs(yPos(0) - yPos(p.y))} fill={s.color} rx={3}
                  onMouseEnter={() => setHover({ s: si, i })} onFocus={() => setHover({ s: si, i })} tabIndex={0}>
                  <title>{`${s.name} ${p.label}: ${p.y}${unit ? ` ${unit}` : ''}${p.flag ? ` (${p.flag})` : ''}`}</title>
                </rect>
              ));
            }
            const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${xPos(p.x).toFixed(1)},${yPos(p.y).toFixed(1)}`).join(' ');
            return (
              <g key={s.name}>
                <path d={d} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                {pts.map((p, i) => {
                  const abnormal = p.flag && p.flag !== 'N';
                  return (
                    <circle key={p.x} cx={xPos(p.x)} cy={yPos(p.y)} r={hover?.s === si && hover.i === i ? 6 : 4} fill={abnormal ? '#d1434b' : s.color} stroke="#fff" strokeWidth={1.5}
                      onMouseEnter={() => setHover({ s: si, i })} onFocus={() => setHover({ s: si, i })} tabIndex={0}>
                      <title>{`${s.name} ${p.label}: ${p.y}${unit ? ` ${unit}` : ''}${p.flag ? ` (${p.flag})` : ''}`}</title>
                    </circle>
                  );
                })}
              </g>
            );
          })}
        </svg>
      ) : (
        <div style={{ height }} aria-hidden="true" />
      )}
      {hovered && hoveredSeries ? (
        <div className="chart-tooltip" role="status" style={{ position: 'absolute', left: Math.min(Math.max(0, xPos(hovered.x) - 70), Math.max(0, width - 160)), top: Math.max(0, yPos(hovered.y) - 56), pointerEvents: 'none' }}>
          <div className="chart-tooltip-date">{hovered.label}</div>
          <div className="chart-tooltip-row">
            <span className="chart-tooltip-swatch" style={{ background: hoveredSeries.color }} aria-hidden="true" />
            <span>{hoveredSeries.name}</span>
            <strong>{hovered.y}{unit ? ` ${unit}` : ''}</strong>
            {hovered.flag && hovered.flag !== 'N' ? <span className={`flag flag-${hovered.flag.toLowerCase()}`}>{hovered.flag}</span> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
